import { Suspense, lazy, useEffect, useRef } from 'react';
import { Navigate, Route, Routes, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { BrandingProvider, useBranding } from './lib/branding';
import { setTenantSlugHeader } from './lib/api';
import { useAuth } from './lib/auth';
import { Spinner } from './components/ui';
import { DevBypassBadge } from './components/DevBypass';
import { Landing } from './pages/Landing';
import { TenantLogin } from './pages/TenantLogin';
import { ChangePassword } from './pages/ChangePassword';
import { NuevaClave } from './pages/NuevaClave';
import { NotFound } from './pages/NotFound';
import { PatientLayout } from './patient/PatientLayout';
import { PatientHome } from './patient/PatientHome';
import { IndicatorPage } from './patient/IndicatorPage';
import { ContentListPage } from './patient/ContentListPage';
import { ContentDetailPage } from './patient/ContentDetailPage';
import { SavedPage } from './patient/SavedPage';
import { TreatmentPage as PatientTreatmentPage } from './patient/TreatmentPage';
import { ProfilePage } from './patient/ProfilePage';
import { AdverseEventPage } from './patient/AdverseEventPage';

/**
 * La consola, el backoffice y las pantallas con gráficos se cargan bajo demanda.
 *
 * Un paciente entra desde el teléfono, muchas veces con la señal de una sala de
 * espera, y jamás va a abrir la consola de la institución. No tiene por qué bajar
 * ese código — ni la librería de gráficos, que sola pesa casi 400 KB.
 */
const HistoryPage = lazy(() => import('./patient/HistoryPage').then((m) => ({ default: m.HistoryPage })));

const Login = lazy(() => import('./pages/Login').then((m) => ({ default: m.Login })));
const DemoEntry = lazy(() => import('./pages/DemoEntry').then((m) => ({ default: m.DemoEntry })));
const Register = lazy(() => import('./pages/Register').then((m) => ({ default: m.Register })));
const VerifyEmail = lazy(() => import('./pages/VerifyEmail').then((m) => ({ default: m.VerifyEmail })));
const TenantsPage = lazy(() => import('./admin/TenantsPage').then((m) => ({ default: m.TenantsPage })));
const PharmaPage = lazy(() => import('./admin/PharmaPage').then((m) => ({ default: m.PharmaPage })));
const AdminLayout = lazy(() => import('./admin/AdminLayout').then((m) => ({ default: m.AdminLayout })));
const PlatformEmailLogPage = lazy(() =>
  import('./admin/PlatformEmailLogPage').then((m) => ({ default: m.PlatformEmailLogPage })),
);

const ConsoleLayout = lazy(() => import('./console/ConsoleLayout').then((m) => ({ default: m.ConsoleLayout })));
const DashboardPage = lazy(() => import('./console/DashboardPage').then((m) => ({ default: m.DashboardPage })));
const AlertsPage = lazy(() => import('./console/AlertsPage').then((m) => ({ default: m.AlertsPage })));
const PatientsPage = lazy(() => import('./console/PatientsPage').then((m) => ({ default: m.PatientsPage })));
const PatientDetailPage = lazy(() =>
  import('./console/PatientDetailPage').then((m) => ({ default: m.PatientDetailPage })),
);
const ProgramsPage = lazy(() => import('./console/ProgramsPage').then((m) => ({ default: m.ProgramsPage })));
const ProgramFollowUpPage = lazy(() =>
  import('./console/ProgramFollowUpPage').then((m) => ({ default: m.ProgramFollowUpPage })),
);
const ProgramFormsPage = lazy(() =>
  import('./console/ProgramFormsPage').then((m) => ({ default: m.ProgramFormsPage })),
);
const ProgramAccessPage = lazy(() =>
  import('./console/ProgramAccessPage').then((m) => ({ default: m.ProgramAccessPage })),
);
const ProgramSettingsPage = lazy(() =>
  import('./console/ProgramSettingsPage').then((m) => ({ default: m.ProgramSettingsPage })),
);
const PharmaPage2 = lazy(() =>
  import('./console/PharmaSettingsPage').then((m) => ({ default: m.PharmaSettingsPage })),
);
const ContentPage = lazy(() => import('./console/ContentPage').then((m) => ({ default: m.ContentPage })));
const EmailLogPage = lazy(() => import('./console/EmailLogPage').then((m) => ({ default: m.EmailLogPage })));
const AccountPage = lazy(() => import('./console/AccountPage').then((m) => ({ default: m.AccountPage })));
const PharmacovigilancePage = lazy(() =>
  import('./console/PharmacovigilancePage').then((m) => ({ default: m.PharmacovigilancePage })),
);

export function App() {
  const { loading } = useAuth();

  // Hasta saber si hay sesión no se puede decidir a dónde mandar a nadie. Sin
  // esta espera, un F5 dentro de la app rebota al login y vuelve, con parpadeo.
  if (loading) return <Spinner label="Cargando tu sesión…" />;

  return (
    <Suspense fallback={<Spinner />}>
      <DevBypassBadge />
      <Routes>
        <Route path="/" element={<Landing />} />

        {/* Dx Tech entra por acá y cae dentro de la consola, igual que todos.
            Ya no hay un backoffice aparte: la consola es una sola y el rol
            decide qué se ve. /admin queda como atajo por los enlaces viejos. */}
        {/* Una sola puerta para todos. La cuenta decide el destino: Dx Tech al
            backoffice, la institución a su consola, el paciente a su app. */}
        <Route path="/ingresar" element={<Login />} />
        {/* Donde aterriza el enlace de "olvide mi contraseña". Sin institución
            en la direccion, que es el caso de quien pidio el recupero desde
            /ingresar o todavia no esta anotado en ningun programa. */}
        <Route path="/nueva-clave" element={<NuevaClave />} />
        {/* Anotarse sabiendo solo el codigo. El QR lleva a la direccion del
            institución, pero quien tiene el codigo en un papel no sabe cual es
            —y el codigo alcanza: es unico en toda la plataforma justamente
            para resolver la institución sin preguntarselo a nadie. */}
        <Route path="/registro" element={<Register />} />
        {/* Recorrida sin credenciales y sin tocar datos de ninguna institución. */}
        <Route path="/simulacion" element={<DemoEntry />} />
        {/* Misma pantalla, no un redirect: /admin/login es la URI de retorno
            registrada en Azure y el popup de MSAL tiene que poder leer la
            respuesta ahí. Un Navigate le cambia la URL antes de que alcance. */}
        <Route path="/admin/login" element={<Login />} />
        {/* Area de Dx Tech: solo lo de plataforma. Para ver una pharma se
            entra en ella, y la consola avisa donde se esta parado. */}
        <Route path="/admin" element={<AdminLayout />}>
          <Route index element={<TenantsPage />} />
          {/* Lo administrativo de una pharma —cuentas, claves— en pantalla
              propia y no en un dialogo sobre el listado. */}
          <Route path="pharmas/:tenantId" element={<PharmaPage />} />
          {/* Los correos de toda la plataforma, incluidos los que no cuelgan de
              ninguna institución: una recuperacion de clave pedida desde
              /ingresar no aparece en ninguna consola, y es la que mas se busca. */}
          <Route path="correos" element={<PlatformEmailLogPage />} />
          <Route path="perfil" element={<AccountPage />} />
        </Route>

        {/* Todo lo que cuelga de un slug vive dentro de la marca de la institución. */}
        <Route path="/:slug/*" element={<TenantRoutes />} />

        <Route path="*" element={<NotFound />} />
      </Routes>
    </Suspense>
  );
}

/**
 * Rutas de un programa.
 *
 * El BrandingProvider envuelve tanto el login como la app: la pantalla de
 * ingreso ya tiene que mostrar la marca de la institución, y en ese momento
 * todavía no hay sesión.
 */
/**
 * Las secciones de la app. Lo que no es ninguna de estas es un programa.
 *
 * Se declaran acá porque el primer segmento después de la institución puede ser
 * una sección o el slug de un programa, y de eso depende con qué marca se pinta
 * la pantalla de ingreso.
 */
const SECCIONES = new Set(['app', 'console', 'cambiar-clave', 'nueva-clave', 'ingresar', 'admin']);

function TenantRoutes() {
  const { slug } = useParams<{ slug: string }>();
  const location = useLocation();

  useTenantScope(slug ?? null);

  /**
   * El programa por el que se entró, si se entró por uno.
   *
   * Con esto la pantalla de ingreso muestra la marca del acompañamiento y no la
   * de la institución: dos programas de la misma pharma tenían la misma puerta y
   * la persona no reconocía dónde estaba entrando.
   */
  const primero = location.pathname.split('/').filter(Boolean)[1] ?? null;
  const programSlug = primero && !SECCIONES.has(primero) ? primero : null;

  return (
    <BrandingProvider slug={slug ?? null} programSlug={programSlug}>
      <CanonicalSlugRedirect />
      <Routes>
        <Route path="/" element={<TenantEntry />} />
        <Route path="/cambiar-clave" element={<ChangePassword />} />
        {/* Va antes de /:programSlug: sin esto la direccion se leia como el
            slug de un programa y el enlace del correo terminaba mostrando la
            pantalla de ingreso, con el token tirado. */}
        <Route path="/nueva-clave" element={<NuevaClave />} />

        {/* A donde lleva el QR: se anota sin sesion, con el codigo del
            programa. Va antes de /app y /console para que un slug de programa
            no se confunda con una seccion. */}
        <Route path="/:programSlug/registro" element={<Register />} />
        <Route path="/:programSlug/verificar" element={<VerifyEmail />} />
        {/* El ingreso por el enlace de un programa: misma pantalla, pintada con
            la marca de ese programa. Va después de las dos de arriba y antes de
            las secciones, que ganan por ser segmentos fijos. */}
        <Route path="/:programSlug" element={<TenantEntry />} />

        <Route path="/app" element={<PatientLayout />}>
          <Route index element={<PatientHome />} />
          {/* Un indicador, un valor. La ruta lleva su identificador y no
              una clave de herramienta: la herramienta dejó de existir. */}
          <Route path="registrar/:indicatorId" element={<IndicatorPage />} />
          <Route path="historial/:indicatorId" element={<HistoryPage />} />
          <Route path="contenidos" element={<ContentListPage />} />
          <Route path="contenidos/:contentSlug" element={<ContentDetailPage />} />
          <Route path="guardados" element={<SavedPage />} />
          {/* La direccion vieja sigue entrando: hay enlaces guardados. */}
          <Route path="calendario" element={<PatientTreatmentPage />} />
          <Route path="tratamiento" element={<Navigate to="../calendario" replace />} />
          <Route path="reportar" element={<AdverseEventPage />} />
          <Route path="perfil" element={<ProfilePage />} />
        </Route>

        {/* La consola gira alrededor del programa.
            Al entrar se ven todos y se elige uno; de ahí en más todo lo que se
            mira —su gente, sus alertas, lo que se le pide, sus contenidos, su
            marca— es de ese programa, y el selector del encabezado salta entre
            ellos sin salir de la sección. Lo único que queda afuera es lo del
            institución, que no depende de ningún programa. */}
        <Route path="/console" element={<ConsoleLayout />}>
          <Route index element={<ProgramsPage />} />

          <Route path="programas/:programSlug">
            <Route index element={<DashboardPage />} />
            <Route path="alertas" element={<AlertsPage />} />
            <Route path="pacientes" element={<PatientsPage />} />
            <Route path="pacientes/:patientId" element={<PatientDetailPage />} />
            <Route path="farmacovigilancia" element={<PharmacovigilancePage />} />
            <Route path="seguimiento" element={<ProgramFollowUpPage />} />
            <Route path="aplicacion" element={<ProgramFormsPage />} />
            <Route path="contenidos" element={<ContentPage />} />
            <Route path="marca" element={<ProgramSettingsPage />} />
            <Route path="acceso" element={<ProgramAccessPage />} />
            <Route path="correos" element={<EmailLogPage />} />
          </Route>

          {/* Lo de la pharma: su marca, su dirección y los correos que salieron. */}
          <Route path="institución" element={<PharmaPage2 />} />
          <Route path="perfil" element={<AccountPage />} />

          {/* Las direcciones viejas siguen entrando: hay pestañas abiertas y
              enlaces guardados que apuntan a "tratamientos". */}
          <Route path="tratamientos" element={<Navigate to="../" replace />} />
          <Route path="marca" element={<Navigate to="../institución" replace />} />
        </Route>

        <Route path="*" element={<NotFound />} />
      </Routes>
    </BrandingProvider>
  );
}


/**
 * Lleva la barra de direcciones a la dirección actual de la institución.
 *
 * Una institución puede renombrarse, y la dirección vieja sigue entrando porque
 * queda como alias — si no, se romperían los enlaces de los mails ya enviados y
 * lo que el paciente se guardó en el teléfono. Pero seguir navegando con la
 * dirección vieja en la URL deja al paciente compartiendo un enlace que ya no
 * es el bueno, así que apenas responde la marca se corrige.
 *
 * `replace` y no `push`: el enlace viejo no tiene por qué quedar en el historial
 * del navegador, donde el botón de volver lo traería de nuevo.
 */
function CanonicalSlugRedirect() {
  const { slug, canonicalSlug } = useBranding();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (!slug || !canonicalSlug || canonicalSlug === slug) return;

    const rest = location.pathname.slice(`/${slug}`.length);
    navigate(`/${canonicalSlug}${rest}${location.search}${location.hash}`, { replace: true });
  }, [slug, canonicalSlug, location, navigate]);

  return null;
}

/**
 * Fija el programa en el que está operando un superadmin de Dx Tech.
 *
 * Para un usuario de la institución no hace nada: su `tenantId` ya viene en el
 * token. Para un superadmin es lo único que le permite entrar a la consola de un
 * cliente — sin esto, la API responde 403 en todas las secciones.
 *
 * Además vacía la caché de consultas al cambiar de programa: los datos que
 * quedaron de una institución no pueden mostrarse bajo otro, aunque la API ya los
 * haya filtrado bien del lado del servidor.
 */
function useTenantScope(slug: string | null): void {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const scope = user?.role === 'SUPERADMIN' ? slug : null;

  // Se fija durante el render y no dentro de un efecto: React ejecuta los
  // efectos de los hijos antes que los del padre, así que para cuando corriera
  // este, la consola ya habría disparado sus consultas sin el header y se
  // habría comido un 403 en todas las secciones.
  //
  // Es seguro hacerlo acá porque `setTenantSlugHeader` es idempotente y el valor
  // se deriva sólo del rol y de la URL: que React lo llame dos veces en modo
  // estricto da exactamente el mismo resultado.
  setTenantSlugHeader(scope);

  const previous = useRef<string | null>(scope);

  useEffect(() => {
    if (previous.current === scope) return;

    /**
     * Cambió de programa: lo que quedó cacheado es del anterior y no puede
     * mostrarse acá, aunque la API ya lo filtre bien del lado del servidor.
     *
     * `reset` y no `clear`. `clear` saca las consultas del caché de raíz, y las
     * que están montadas en ese momento se quedan sin nadie que las vuelva a
     * pedir: la pantalla queda cargando para siempre. Pasaba al cambiar de
     * programa desde cualquier sección que no fuera el tablero. `reset` descarta
     * los datos igual, pero vuelve a disparar las consultas activas — que para
     * entonces ya salen con el header del programa nuevo, porque se fija durante
     * el render y no en un efecto.
     */
    void queryClient.resetQueries();
    previous.current = scope;
  }, [scope, queryClient]);

  // Al salir del programa —por ejemplo al volver al backoffice— el header deja
  // de tener sentido.
  useEffect(() => () => setTenantSlugHeader(null), []);
}

/** Manda a cada rol a donde corresponde, o al login si no hay sesión. */
function TenantEntry() {
  const { user } = useAuth();
  const { slug } = useParams<{ slug: string }>();

  if (!user) return <TenantLogin />;
  if (user.mustChangePassword) return <Navigate to={`/${slug}/cambiar-clave`} replace />;

  // Un usuario logueado en otro programa que abre este slug tiene que ver el
  // login de este, no la app del anterior con la marca equivocada.
  if (user.tenantSlug && user.tenantSlug !== slug && user.role !== 'SUPERADMIN') {
    return <TenantLogin />;
  }

  if (user.role === 'PATIENT' || user.role === 'CAREGIVER') {
    return <Navigate to={`/${slug}/app`} replace />;
  }

  return <Navigate to={`/${slug}/console`} replace />;
}
